import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Network, FileSearch, ScrollText, UserCheck, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ParticleField from '../components/ParticleField';
import FloatingLogo from '../components/FloatingLogo';
import { Reveal, } from '../components/Footer';
import { CtaBand } from './About';
import { brand, eiomPrinciples, eiomOutcomes, eiomUseCases } from '../mock';

const icons = [ShieldCheck, Network, FileSearch, ScrollText, UserCheck];

export default function EIOM() {
  return (
    <div className="App">
      <section className="hero-dark relative overflow-hidden">
        <ParticleField density={1} />
        <div className="absolute inset-0 hidden md:block pointer-events-none">
          <FloatingLogo src={brand.logoTransparent} size={200} speed={0.5} vary />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 pt-40 pb-24 md:pt-48 md:pb-28">
          <Reveal className="mx-auto max-w-4xl text-center">
            <h1 className="mt-5 font-serif text-[36px] md:text-[56px] leading-[1.08] text-white">
              A governed operating model for enterprise intelligence
            </h1>
            <p className="mt-6 mx-auto max-w-2xl text-[17px] leading-relaxed text-[#c4d0cd]">
              EIOM operates as an intelligence layer around AI, providing the structure through which intelligence is sourced, evaluated, applied and held accountable across enterprise workflows.
            </p>
            <span className="mt-7 inline-block rounded-full border border-[#c6a94b]/40 bg-[#c6a94b]/10 px-4 py-1.5 text-[12px] font-semibold tracking-wide text-[#e0c877]">
              Proprietary IP of Herbert Project Partners
            </span>
          </Reveal>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent to-[#faf9f5]" />
      </section>

      {/* Principles */}
      <section className="relative overflow-hidden bg-[#faf9f5] py-24">
        <ParticleField density={0.55} variant="light" />
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#1f5866]">Core principles</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-tight text-[#22312f]">Five principles that hold it together</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {eiomPrinciples.map((p, i) => {
              const Icon = icons[i % icons.length];
              return (
                <Reveal key={p.title} delay={i * 80}>
                  <div className="lift h-full rounded-2xl border border-[#eceae2] bg-white p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eef3f1]">
                      <Icon className="h-6 w-6 text-[#1f5866]" />
                    </div>
                    <h3 className="mt-5 font-serif text-[24px] text-[#22312f]">{p.title}</h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#5a6668]">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="bg-white py-24 border-y border-[#eceae2]">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="eyebrow text-[#c6a94b]">Intended outcomes</p>
            <h2 className="mt-4 font-serif text-[34px] md:text-[46px] leading-tight text-[#22312f]">Business outcomes, not technical complexity</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-[#5a6668]">
              EIOM is Herbert Project Partners’ proprietary model for turning AI into governed interoperable capability. We publish its value, outcomes and operating principles.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <ul className="space-y-3">
              {eiomOutcomes.map((o) => (
                <li key={o} className="flex items-center gap-3 rounded-xl border border-[#eceae2] bg-[#faf9f5] px-5 py-4">
                  <CheckCircle2 className="h-5 w-5 text-[#c6a94b] shrink-0" />
                  <span className="text-[15px] text-[#22312f]">{o}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Use cases */}
      <section className="relative overflow-hidden bg-[#faf9f5] py-24">
        <ParticleField density={0.55} variant="light" />
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <Reveal className="mx-auto max-w-4xl text-center">
            <p className="eyebrow text-[#1f5866]">Typical use cases</p>
            <h2 className="mt-4 font-serif text-[32px] md:text-[44px] leading-tight text-[#22312f]">Where the Enterprise Intelligence Operating Model is applied</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {eiomUseCases.map((u, i) => (
              <Reveal key={u.title} delay={i * 90}>
                <div className="lift h-full rounded-2xl border border-[#eceae2] bg-white p-8">
                  <span className="font-serif text-[30px] text-[#c6a94b]">0{i + 1}</span>
                  <h3 className="mt-3 font-serif text-[22px] text-[#22312f]">{u.title}</h3>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-[#5a6668]">{u.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Safeguard note */}
      <section className="bg-white py-20 border-t border-[#eceae2]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-2xl border border-[#e7e5dd] bg-[#faf9f5] p-8 md:p-10">
              <p className="eyebrow text-[#c6a94b]">A note on our IP</p>
              <p className="mt-4 text-[16px] leading-relaxed text-[#3f4d4b]">
                We deliberately publish EIOM’s value, outcomes and operating principles; not detailed schemas, control logic, prompt libraries or implementation methods. This protects both our intellectual property and the integrity of the governance we provide.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
